import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Search } from "lucide-react";
import api from "@/lib/api";

const ACTION_LABEL = {
  create: "إنشاء", update: "تعديل", archive: "أرشفة", restore: "استعادة",
  activate: "تفعيل حساب", deactivate: "تعطيل حساب", sale: "عملية بيع", web_order: "طلب من الموقع",
  return_requested: "طلب إرجاع/استبدال", return_approved: "الموافقة على إرجاع", return_rejected: "رفض إرجاع",
  return_reopened: "إعادة فتح إرجاع",
  password_reset: "إعادة تعيين رقم سري", pos_order: "فاتورة نقطة بيع", daily_closing: "إقفال يومي",
  order_status: "تغيير حالة الطلب", cash_confirmed: "تأكيد دفع نقدي",
  invoice_edit: "تعديل فاتورة", invoice_cancel: "إلغاء فاتورة",
  web_payment_paid: "تأكيد دفع طلب موقع", web_payment_failed: "فشل دفع طلب موقع", web_payment_cancelled: "إلغاء دفع طلب موقع",
  content_publish: "نشر محتوى", content_revert: "استرجاع نسخة محتوى",
};
const ENTITY_LABEL = { product: "منتج", user: "مستخدم", invoice: "فاتورة", order: "طلب", return: "إرجاع", settings: "إعدادات", policy: "سياسة", content: "محتوى الموقع" };
const META_KEY = {
  order_no: "رقم الطلب", invoice_no: "رقم الفاتورة", reason: "السبب", status: "الحالة",
  total: "الإجمالي", rev: "نسخة المراجعة", role: "الدور", username: "اسم المستخدم", email: "البريد",
  name: "الاسم", payment_method: "طريقة الدفع", version: "الإصدار", fields: "الحقول",
  sessions_ended: "إنهاء الجلسات", section: "القسم",
};
const STATUS_TERM = {
  AWAITING_PAYMENT: "بانتظار الدفع", PAID: "مدفوع", MEASUREMENTS_REVIEW: "مراجعة المقاسات",
  IN_PRODUCTION: "قيد التفصيل", READY: "جاهز", DELIVERED: "تم التسليم", CANCELLED: "ملغي",
};
const fmtVal = (v) => (STATUS_TERM[v] || (v === true ? "نعم" : v === false ? "لا" : v));

const WEB_ACTIONS = new Set(["web_order", "web_payment_paid", "web_payment_failed", "web_payment_cancelled"]);
function actorDisplay(r) {
  if (r.actor_name) return r.actor_name + (r.actor_role === "owner" ? " (الأونر)" : r.actor_role === "seller" ? " (عاملة)" : "");
  if (WEB_ACTIONS.has(r.action)) return "الموقع (عميلة)";
  if (r.actor_email) return r.actor_email;
  return "غير موثق";
}

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [actionF, setActionF] = useState("");
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const actions = useMemo(() => Array.from(new Set(rows.map((r) => r.action))), [rows]);
  const filtered = rows.filter((r) => {
    if (actionF && r.action !== actionF) return false;
    if (!q) return true;
    const hay = `${r.actor_name || ""} ${r.actor_email} ${ACTION_LABEL[r.action] || r.action} ${JSON.stringify(r.meta || {})}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="p-5 md:p-8">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-accent" strokeWidth={1.5} />
        <h1 className="font-display text-3xl">سجل التدقيق</h1>
      </div>
      <p className="text-sm text-text-muted mb-5">سجل غير قابل للتعديل أو الحذف — يُحفظ كل إجراء تلقائياً.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في السجل…" data-testid="audit-search"
            className="w-full bg-white border border-border ps-10 pe-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <select value={actionF} onChange={(e) => setActionF(e.target.value)} data-testid="audit-filter-action"
          className="bg-white border border-border px-3 py-2.5 text-sm">
          <option value="">كل الإجراءات</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] || a}</option>)}
        </select>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-text-muted text-xs">
            <tr>
              <th className="text-start p-3">الوقت</th>
              <th className="text-start p-3">الإجراء</th>
              <th className="text-start p-3">العنصر</th>
              <th className="text-start p-3">المستخدم</th>
              <th className="text-start p-3">التفاصيل</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={i} className="border-t border-border" data-testid={`audit-row-${i}`}>
                <td className="p-3 text-text-muted whitespace-nowrap">{new Date(r.created_at).toLocaleString("ar-KW")}</td>
                <td className="p-3">{ACTION_LABEL[r.action] || r.action}</td>
                <td className="p-3">{ENTITY_LABEL[r.entity] || r.entity}</td>
                <td className="p-3 text-text-muted" data-testid={`audit-actor-${i}`}>{actorDisplay(r)}</td>
                <td className="p-3 text-xs text-text-muted">
                  {Object.entries(r.meta || {}).map(([k, v]) => `${META_KEY[k] || k}: ${fmtVal(v)}`).join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-8 text-center text-text-muted text-sm">لا توجد سجلات مطابقة</p>}
      </div>
    </div>
  );
}
